import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import Confirm, { ConfirmOptions } from '../common/Confirm';

type DialogContextType = {
  showConfirm: (options: ConfirmOptions) => void;
  hideConfirm: () => void;
};

// dialog 사용 예시
// const { showToast, showConfirm, showAlert } = useDialog();
// const handleError = () => {
//   showAlert({
//     title: '오류',
//     message: '오류가 발생했습니다.',
//   });
// };

// dialog를 전역적으로 관리하기 위한 context provider
const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions | null>(
    null,
  );
  const showConfirm = useCallback((options: ConfirmOptions) => {
    setConfirmOptions(options);
  }, []);

  const hideConfirm = useCallback(() => {
    setConfirmOptions(null);
  }, []);

  const handleConfirmClose = useCallback(() => {
    confirmOptions?.onClose?.();
    hideConfirm();
  }, [confirmOptions, hideConfirm]);

  /** closeButton(X) 클릭 시: onClosePress 있으면 호출, 없으면 onClose 호출 후 모달 닫기 */
  const handleConfirmClosePress = useCallback(() => {
    (confirmOptions?.onClosePress ?? confirmOptions?.onClose)?.();
    hideConfirm();
  }, [confirmOptions, hideConfirm]);

  const handleConfirmConfirm = useCallback(() => {
    confirmOptions?.onConfirm();
    hideConfirm();
  }, [confirmOptions, hideConfirm]);

  return (
    <DialogContext.Provider
      value={{
        showConfirm,
        hideConfirm,
      }}
    >
      {children}
      {confirmOptions && (
        <Confirm
          {...confirmOptions}
          onConfirm={handleConfirmConfirm}
          onClose={handleConfirmClose}
          onClosePress={handleConfirmClosePress}
        />
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
};
